import type { DeviceStatus, Flow } from '../api/types';

/**
 * live = a live flow names the device; online = a WireGuard handshake within ONLINE_WINDOW_MS;
 * otherwise offline. Every screen draws its device dots from this one rule.
 */
export type Presence = 'live' | 'online' | 'offline';

/** WireGuard re-handshakes every 2 min while there is traffic; one missed is a minute's grace. */
export const ONLINE_WINDOW_MS = 3 * 60_000;

/** Device ids at either end of a live flow (GET /flows `live`). */
export function liveDeviceIds(flows: readonly Flow[] | null | undefined): Set<string> {
  const ids = new Set<string>();
  for (const f of flows ?? []) {
    if (!f.live) continue;
    if (f.src.deviceId) ids.add(f.src.deviceId);
    if (f.dst.deviceId) ids.add(f.dst.deviceId);
  }
  return ids;
}

export function presence(
  deviceId: string,
  status: DeviceStatus | null | undefined,
  live: ReadonlySet<string>,
  now: number = Date.now(),
): Presence {
  if (live.has(deviceId)) return 'live';
  const t = status?.lastHandshakeAt ? Date.parse(status.lastHandshakeAt) : NaN;
  return Number.isFinite(t) && now - t <= ONLINE_WINDOW_MS ? 'online' : 'offline';
}

/** Online or live. */
export const isOnline = (p: Presence) => p !== 'offline';

/** Ids of the `devices` that are online or live. */
export function onlineIds(
  devices: readonly { id: string }[],
  statuses: readonly DeviceStatus[] | null | undefined,
  live: ReadonlySet<string>,
  now: number = Date.now(),
): Set<string> {
  const byId = new Map((statuses ?? []).map((s) => [s.deviceId, s]));
  return new Set(devices.filter((d) => isOnline(presence(d.id, byId.get(d.id), live, now))).map((d) => d.id));
}
